import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useEffect, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { useToast } from '@ais-ui/ui-core';
import { useSaveSettings } from '@ais-ui/websockets';
import { useSettings } from '../SettingsProvider';
import { useSettingsPage } from '../SettingsPageProvider';

// Schema for admin settings
export const adminFormSchema = z.object({
  // Target settings
  showTargetCountBadge: z.boolean(),
});

export type AdminFormValues = z.infer<typeof adminFormSchema>;

export const useAdminForm = () => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const { settings } = useSettings();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { mutate: mutateSettings } = useSaveSettings();
  const { setUnsavedSettings } = useSettingsPage();

  const initialValues: AdminFormValues = {
    showTargetCountBadge: settings?.showTargetCountBadge ?? false,
  };

  const form = useForm<AdminFormValues>({
    resolver: zodResolver(adminFormSchema),
    defaultValues: initialValues,
  });

  const { setValue, handleSubmit } = form;

  // Watch for form changes to track unsaved state - following the same pattern as General form
  useEffect(() => {
    const subscription = form.watch((formValues) => {
      const hasChanges =
        JSON.stringify(formValues) !== JSON.stringify(initialValues);
      setUnsavedSettings(hasChanges);
    });

    return () => subscription.unsubscribe();
  }, [form, form.watch, initialValues, setUnsavedSettings]);

  // Initialize form with current settings
  useEffect(() => {
    if (settings) {
      setValue('showTargetCountBadge', settings.showTargetCountBadge ?? false);
    }
  }, [settings, setValue]);

  const onSubmit = async (data: AdminFormValues) => {
    setIsSubmitting(true);
    try {
      // Merge admin settings with existing settings
      const updatedSettings = {
        ...settings,
        showTargetCountBadge: data.showTargetCountBadge,
      };
      await mutateSettings(updatedSettings);
      toast({
        title: messages.genericToastTitleSuccess,
        description: messages.settingsAdminSuccessDescription,
      });
    } catch (error) {
      toast({
        title: messages.genericToastTitleError,
        description: messages.settingsAdminErrorDescription,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    isSubmitting,
    onSubmit: handleSubmit(onSubmit),
  };
};
